import { formatPence, formatPercentBp, marginBp, pence, vatOn } from '@hcd/domain'
import { Archive, ArchiveRestore, Loader2, Pencil, Plus, Search, Tags } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { can } from '@/lib/roles'
import type { StaffUser } from '@/lib/session'
import {
  useResource,
  useResourceMutations,
  type NamedItem,
  type PriceItem,
  type VatRate,
} from '@/lib/settings'
import { cn } from '@/lib/utils'
import { FormField, MoneyInput, NativeSelect, ReadOnlyNote, TextInput } from './form-kit'
import { PageTitle } from './pages'
import { ResourceManager } from './resource-manager'

const FREQ_LABEL = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
} as const
const chargeLabel = (i: Pick<PriceItem, 'chargeType' | 'frequency'>) =>
  i.chargeType === 'one_off' ? 'One-off' : i.frequency ? FREQ_LABEL[i.frequency] : 'Recurring'

type Draft = Omit<PriceItem, 'id' | 'active' | 'version' | 'updatedAt' | 'sellPence'> & {
  sellPence: number | null
}

function MarginBadge({ sell, cost }: { sell: number; cost: number }) {
  const m = marginBp(pence(sell), pence(cost))
  if (m === null || cost === 0) return <span className="text-muted-foreground">No cost set</span>
  const tone = m < 0 ? 'text-destructive' : m < 2000 ? 'text-warning' : 'text-success'
  return <span className={cn('tabular font-medium', tone)}>{formatPercentBp(m)} margin</span>
}

export function PriceBookPage({ me }: { me: StaffUser }) {
  const editable = can.managePriceBook(me)
  const items = useResource<PriceItem>('price-items')
  const units = useResource<NamedItem>('price-units')
  const cats = useResource<NamedItem>('price-categories')
  const vat = useResource<VatRate>('vat-rates')
  const { create, update } = useResourceMutations<PriceItem>('price-items', 'price item')

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [charge, setCharge] = useState<'all' | 'one_off' | 'recurring'>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState<{ item: PriceItem | null; draft: Draft } | null>(null)

  const byId = <T extends { id: string }>(list: T[] | undefined) =>
    new Map((list ?? []).map((x) => [x.id, x]))
  const unitMap = byId(units.data)
  const catMap = byId(cats.data)
  const vatMap = byId(vat.data)
  const defaultVat = vat.data?.find((v) => v.isDefault && v.active)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (items.data ?? []).filter(
      (i) =>
        (showArchived || i.active) &&
        (category === 'all' || (category === 'none' ? !i.categoryId : i.categoryId === category)) &&
        (charge === 'all' || i.chargeType === charge) &&
        (!q || `${i.name} ${i.code} ${i.description}`.toLowerCase().includes(q)),
    )
  }, [items.data, query, category, charge, showArchived])

  const blank = (): Draft => ({
    code: '',
    name: '',
    description: '',
    internalNotes: '',
    categoryId: category !== 'all' && category !== 'none' ? category : null,
    unitId:
      units.data?.find((u) => u.active && u.name === 'visit')?.id ??
      units.data?.find((u) => u.active)?.id ??
      '',
    chargeType: 'recurring',
    frequency: 'monthly',
    sellPence: null,
    costPence: 0,
    minimumPence: 0,
    vatRateId: defaultVat?.id ?? '',
    sort: 0,
  })

  const loading = items.isPending || units.isPending || cats.isPending || vat.isPending
  const archivedCount = (items.data ?? []).filter((i) => !i.active).length

  return (
    <>
      <PageTitle
        title="Price book"
        description="Your standard services and products. Quotes copy the price at the time, so changing a price here never changes a quote already approved."
      />
      {!editable && <ReadOnlyNote who="owners, platform admins and commercial managers" />}

      <Tabs defaultValue="items" className="gap-4">
        <TabsList className="h-11 w-full sm:w-auto">
          <TabsTrigger value="items" className="px-4">
            Items{' '}
            <span className="tabular text-muted-foreground">
              {(items.data ?? []).filter((i) => i.active).length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="px-4">
            Categories
          </TabsTrigger>
          <TabsTrigger value="units" className="px-4">
            Units
          </TabsTrigger>
        </TabsList>
        <TabsContent value="items">
          <section className="rounded-xl border bg-card">
            <div className="flex flex-wrap items-center gap-3 border-b p-4">
              <div className="relative min-w-0 flex-1 basis-60">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  aria-label="Search the price book"
                  placeholder="Search by name or code"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-11 pl-9 text-base md:h-10 md:text-[15px]"
                />
              </div>
              <NativeSelect
                aria-label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-auto min-w-40 flex-1 basis-44 md:flex-none"
              >
                <option value="all">All categories</option>
                {(cats.data ?? [])
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                <option value="none">No category</option>
              </NativeSelect>
              <NativeSelect
                aria-label="Charge type"
                value={charge}
                onChange={(e) => setCharge(e.target.value as typeof charge)}
                className="w-auto flex-1 basis-32 md:flex-none"
              >
                <option value="all">One-off and recurring</option>
                <option value="one_off">One-off only</option>
                <option value="recurring">Recurring only</option>
              </NativeSelect>
              {editable && (
                <Button
                  className="h-11 md:h-10"
                  onClick={() => setEditing({ item: null, draft: blank() })}
                  disabled={loading}
                >
                  <Plus />
                  Add item
                </Button>
              )}
            </div>

            {loading ? (
              <div className="grid gap-2 p-4">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : (items.data ?? []).length === 0 ? (
              <Empty className="py-12">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Tags />
                  </EmptyMedia>
                  <EmptyTitle>Your price book is empty</EmptyTitle>
                  <EmptyDescription>
                    Add the services you quote most often, such as an office clean per visit or a
                    holiday let changeover, with your sell price and cost.
                  </EmptyDescription>
                </EmptyHeader>
                {editable && (
                  <EmptyContent>
                    <Button
                      className="h-10"
                      onClick={() => setEditing({ item: null, draft: blank() })}
                    >
                      <Plus />
                      Add your first item
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-[15px] text-muted-foreground">
                No items match. Try a different search or category.
              </p>
            ) : (
              <>
                {/* Desktop and tablet landscape: table */}
                <div className="hidden lg:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-4">Item</TableHead>
                        <TableHead>Charged</TableHead>
                        <TableHead className="text-right">Sell</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead>Margin</TableHead>
                        <TableHead>VAT</TableHead>
                        {editable && (
                          <TableHead className="w-24 pr-4 text-right">
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((i) => (
                        <TableRow key={i.id} className={cn(!i.active && 'opacity-60')}>
                          <TableCell className="max-w-80 pl-4 whitespace-normal">
                            <div className="font-medium">
                              {i.name} {!i.active && <Badge variant="outline">Archived</Badge>}
                            </div>
                            <div className="text-[13px] text-muted-foreground">
                              {[i.code, i.categoryId ? catMap.get(i.categoryId)?.name : null]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={i.chargeType === 'one_off' ? 'outline' : 'secondary'}>
                              {chargeLabel(i)}
                            </Badge>
                            <div className="mt-1 text-[13px] text-muted-foreground">
                              per {unitMap.get(i.unitId)?.name ?? 'unit'}
                            </div>
                          </TableCell>
                          <TableCell className="tabular text-right font-medium">
                            {formatPence(i.sellPence)}
                          </TableCell>
                          <TableCell className="tabular text-right text-muted-foreground">
                            {i.costPence ? formatPence(i.costPence) : '—'}
                          </TableCell>
                          <TableCell>
                            <MarginBadge sell={i.sellPence} cost={i.costPence} />
                          </TableCell>
                          <TableCell className="tabular">
                            {vatMap.get(i.vatRateId)
                              ? formatPercentBp(vatMap.get(i.vatRateId)!.rateBp)
                              : '—'}
                          </TableCell>
                          {editable && (
                            <TableCell className="pr-4 text-right">
                              <RowActions
                                item={i}
                                onEdit={() => setEditing({ item: i, draft: { ...i } })}
                                onToggle={() =>
                                  update.mutate({
                                    id: i.id,
                                    active: !i.active,
                                    expectedVersion: i.version,
                                  })
                                }
                                busy={update.isPending}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Phones and tablet portrait: cards */}
                <ul className="divide-y lg:hidden">
                  {filtered.map((i) => (
                    <li key={i.id} className={cn('flex gap-3 p-4', !i.active && 'opacity-60')}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">
                          {i.name} {!i.active && <Badge variant="outline">Archived</Badge>}
                        </div>
                        <div className="mt-0.5 text-[13px] text-muted-foreground">
                          {chargeLabel(i)} · per {unitMap.get(i.unitId)?.name ?? 'unit'}
                          {i.categoryId && catMap.get(i.categoryId)
                            ? ` · ${catMap.get(i.categoryId)!.name}`
                            : ''}
                        </div>
                        <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                          <span className="tabular text-base font-semibold">
                            {formatPence(i.sellPence)}
                          </span>
                          <MarginBadge sell={i.sellPence} cost={i.costPence} />
                        </div>
                      </div>
                      {editable && (
                        <RowActions
                          item={i}
                          onEdit={() => setEditing({ item: i, draft: { ...i } })}
                          onToggle={() =>
                            update.mutate({
                              id: i.id,
                              active: !i.active,
                              expectedVersion: i.version,
                            })
                          }
                          busy={update.isPending}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {archivedCount > 0 && (
              <label className="flex items-center gap-2 border-t px-4 py-3 text-sm text-muted-foreground">
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
                Show archived items ({archivedCount})
              </label>
            )}
          </section>
        </TabsContent>

        <TabsContent value="categories">
          <ResourceManager<NamedItem>
            path="price-categories"
            noun="category"
            title="Categories"
            description="Group items in the price book and in quote sections."
            canEdit={editable}
            empty="No categories yet."
            blank={{ name: '', sort: 0 }}
            row={(c) => ({ primary: c.name })}
            toInput={(d) => ({ name: d.name, sort: d.sort })}
            form={(d, set) => nameForm(d, set)}
          />
        </TabsContent>
        <TabsContent value="units">
          <ResourceManager<NamedItem>
            path="price-units"
            noun="unit"
            title="Units"
            description="What a price is per: visit, hour, room, m² and so on."
            canEdit={editable}
            empty="No units yet."
            blank={{ name: '', sort: 0 }}
            row={(u) => ({ primary: u.name })}
            toInput={(d) => ({ name: d.name, sort: d.sort })}
            form={(d, set) => nameForm(d, set)}
          />
        </TabsContent>
      </Tabs>

      <ItemSheet
        editing={editing}
        setDraft={(patch) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e))}
        close={() => setEditing(null)}
        units={(units.data ?? []).filter((u) => u.active || u.id === editing?.draft.unitId)}
        cats={(cats.data ?? []).filter((c) => c.active || c.id === editing?.draft.categoryId)}
        vat={(vat.data ?? []).filter((v) => v.active || v.id === editing?.draft.vatRateId)}
        saving={create.isPending || update.isPending}
        onSave={(draft) => {
          // Send only editable fields; never echo record metadata (id, active, version).
          const {
            code,
            name,
            description,
            internalNotes,
            categoryId,
            unitId,
            chargeType,
            frequency,
            costPence,
            minimumPence,
            vatRateId,
            sort,
          } = draft
          const body = {
            code,
            name,
            description,
            internalNotes,
            categoryId,
            unitId,
            chargeType,
            costPence,
            minimumPence,
            vatRateId,
            sort,
            sellPence: draft.sellPence ?? 0,
            frequency: chargeType === 'recurring' ? frequency : null,
          }
          const done = { onSuccess: () => setEditing(null) }
          if (editing?.item)
            update.mutate(
              { ...body, id: editing.item.id, expectedVersion: editing.item.version },
              done,
            )
          else create.mutate(body, done)
        }}
      />
    </>
  )
}

function nameForm(d: Partial<NamedItem>, set: (p: Partial<NamedItem>) => void) {
  const errors: Record<string, string> = {}
  if (!d.name?.trim()) errors.name = 'Enter a name.'
  return {
    errors,
    fields: (
      <FormField label="Name" error={errors.name}>
        {(p) => (
          <TextInput
            {...p}
            autoFocus
            value={d.name ?? ''}
            onChange={(e) => set({ name: e.target.value })}
          />
        )}
      </FormField>
    ),
  }
}

function RowActions({
  item,
  onEdit,
  onToggle,
  busy,
}: {
  item: PriceItem
  onEdit: () => void
  onToggle: () => void
  busy: boolean
}) {
  return (
    <div className="flex shrink-0 justify-end gap-1">
      <Button variant="ghost" size="icon-lg" aria-label={`Edit ${item.name}`} onClick={onEdit}>
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon-lg"
        aria-label={item.active ? `Archive ${item.name}` : `Restore ${item.name}`}
        onClick={onToggle}
        disabled={busy}
      >
        {item.active ? <Archive /> : <ArchiveRestore />}
      </Button>
    </div>
  )
}

function ItemSheet({
  editing,
  setDraft,
  close,
  units,
  cats,
  vat,
  saving,
  onSave,
}: {
  editing: { item: PriceItem | null; draft: Draft } | null
  setDraft: (p: Partial<Draft>) => void
  close: () => void
  units: NamedItem[]
  cats: NamedItem[]
  vat: VatRate[]
  saving: boolean
  onSave: (d: Draft) => void
}) {
  const d = editing?.draft
  const errors: Record<string, string> = {}
  if (d) {
    if (!d.name.trim()) errors.name = 'Enter a name.'
    if (d.sellPence === null) errors.sellPence = 'Enter a price, for example 706.50'
    if (!d.unitId) errors.unitId = 'Choose a unit.'
    if (!d.vatRateId) errors.vatRateId = 'Choose a VAT rate.'
    if (d.chargeType === 'recurring' && !d.frequency) errors.frequency = 'Choose how often.'
    if (d.code && !/^[A-Za-z0-9._-]*$/.test(d.code))
      errors.code = 'Use letters, numbers, dots, dashes or underscores.'
  }
  const valid = Object.keys(errors).length === 0
  const rate = d ? vat.find((v) => v.id === d.vatRateId) : undefined
  const unitName = d ? units.find((u) => u.id === d.unitId)?.name : undefined

  return (
    <Sheet open={!!editing} onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-xl"
      >
        <SheetHeader className="border-b">
          <SheetTitle>{editing?.item ? 'Edit price item' : 'Add price item'}</SheetTitle>
          <SheetDescription>
            Prices exclude VAT. Clients never see the cost or internal notes.
          </SheetDescription>
        </SheetHeader>
        {d && (
          <form
            id="price-item-form"
            className="grid gap-5 p-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (valid) onSave(d)
            }}
          >
            <FormField label="Name" hint="As it will appear on the quote." error={errors.name}>
              {(p) => (
                <TextInput
                  {...p}
                  autoFocus
                  value={d.name}
                  onChange={(e) => setDraft({ name: e.target.value })}
                />
              )}
            </FormField>
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Code" hint="Optional, must be unique." error={errors.code}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={d.code}
                    onChange={(e) => setDraft({ code: e.target.value })}
                  />
                )}
              </FormField>
              <FormField label="Category">
                {(p) => (
                  <NativeSelect
                    {...p}
                    value={d.categoryId ?? ''}
                    onChange={(e) => setDraft({ categoryId: e.target.value || null })}
                  >
                    <option value="">No category</option>
                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </FormField>
            </div>

            <fieldset className="grid gap-2">
              <legend className="mb-1 text-sm font-medium">How it is charged</legend>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    ['recurring', 'Recurring', 'Regular service'],
                    ['one_off', 'One-off', 'Single job'],
                  ] as const
                ).map(([value, label, hint]) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-accent/40"
                  >
                    <input
                      type="radio"
                      name="chargeType"
                      className="mt-1 size-4 accent-(--primary)"
                      checked={d.chargeType === value}
                      onChange={() =>
                        setDraft({
                          chargeType: value,
                          frequency: value === 'recurring' ? (d.frequency ?? 'monthly') : null,
                        })
                      }
                    />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-[13px] text-muted-foreground">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="grid gap-5 sm:grid-cols-2">
              {d.chargeType === 'recurring' && (
                <FormField label="How often" error={errors.frequency}>
                  {(p) => (
                    <NativeSelect
                      {...p}
                      value={d.frequency ?? ''}
                      onChange={(e) =>
                        setDraft({ frequency: (e.target.value || null) as Draft['frequency'] })
                      }
                    >
                      {Object.entries(FREQ_LABEL).map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </FormField>
              )}
              <FormField label="Priced per" error={errors.unitId}>
                {(p) => (
                  <NativeSelect
                    {...p}
                    value={d.unitId}
                    onChange={(e) => setDraft({ unitId: e.target.value })}
                  >
                    <option value="" disabled>
                      Choose a unit
                    </option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </FormField>
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              <FormField label="Sell price" error={errors.sellPence}>
                {(p) => (
                  <MoneyInput
                    {...p}
                    valuePence={d.sellPence}
                    onChangePence={(v) => setDraft({ sellPence: v })}
                  />
                )}
              </FormField>
              <FormField label="Cost" hint="Internal only">
                {(p) => (
                  <MoneyInput
                    {...p}
                    valuePence={d.costPence}
                    onChangePence={(v) => setDraft({ costPence: v ?? 0 })}
                  />
                )}
              </FormField>
              <FormField label="Minimum charge" hint="Optional">
                {(p) => (
                  <MoneyInput
                    {...p}
                    valuePence={d.minimumPence}
                    onChangePence={(v) => setDraft({ minimumPence: v ?? 0 })}
                  />
                )}
              </FormField>
            </div>

            <FormField label="VAT rate" error={errors.vatRateId}>
              {(p) => (
                <NativeSelect
                  {...p}
                  value={d.vatRateId}
                  onChange={(e) => setDraft({ vatRateId: e.target.value })}
                >
                  <option value="" disabled>
                    Choose a VAT rate
                  </option>
                  {vat.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({formatPercentBp(v.rateBp)})
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>

            {d.sellPence !== null && (
              <div className="grid gap-1 rounded-lg bg-muted p-4 text-sm" aria-live="polite">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">
                    Client pays per {unitName ?? 'unit'} (inc. VAT)
                  </span>
                  <span className="tabular font-semibold">
                    {formatPence(d.sellPence + (rate ? vatOn(pence(d.sellPence), rate.rateBp) : 0))}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Margin</span>
                  <MarginBadge sell={d.sellPence} cost={d.costPence} />
                </div>
              </div>
            )}

            <FormField
              label="Description for the client"
              hint="Optional. Shown under the item on proposals."
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={3}
                  value={d.description}
                  onChange={(e) => setDraft({ description: e.target.value })}
                  className="text-base md:text-[15px]"
                />
              )}
            </FormField>
            <FormField label="Internal notes" hint="Only HCD staff can see these.">
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={d.internalNotes}
                  onChange={(e) => setDraft({ internalNotes: e.target.value })}
                  className="text-base md:text-[15px]"
                />
              )}
            </FormField>
          </form>
        )}
        <SheetFooter className="sticky bottom-0 flex-row justify-end gap-2 border-t bg-card">
          <Button type="button" variant="outline" className="h-10" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" form="price-item-form" className="h-10" disabled={!valid || saving}>
            {saving && <Loader2 className="animate-spin" />}
            {editing?.item ? 'Save changes' : 'Add item'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
